import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { createAppThunk } from '../createAppThunk';
import { fetchActivityLogs, fetchActivityLogsByEntityId } from '@/lib/apiClient';
import type { EnrichedActivityLog } from '@/types/Api';
import type { NormalizedError } from '@/types/NormalizedError';
import { OPTIMISTIC_ACTIVITY_LOG_TTL_MS } from '@/redux/optimisticActivityLog';

interface ActivityLogState {
  serverLogs: EnrichedActivityLog[];
  logs: EnrichedActivityLog[];
  byId: Record<string, EnrichedActivityLog>;
  loading: boolean;
  error: NormalizedError | null;
  filters: {
    organizationId?: string;
    entityType?: string;
    entityId?: string;
    actionType?: string;
    fromDate?: string;
    toDate?: string;
  };
  pendingOptimisticEntries: Record<string, EnrichedActivityLog[]>;
  entityLogs: EnrichedActivityLog[];
  entityLoading: boolean;
}

const mergeActivityLogs = (
  serverLogs: EnrichedActivityLog[],
  pendingOptimisticEntries: Record<string, EnrichedActivityLog[]>,
): EnrichedActivityLog[] => {
  const now = Date.now();
  const optimisticLogs = Object.values(pendingOptimisticEntries)
    .flat()
    .filter((entry) => {
      const timestamp = new Date(entry.timestamp).getTime();
      return Number.isFinite(timestamp) && now - timestamp <= OPTIMISTIC_ACTIVITY_LOG_TTL_MS;
    });
  const optimisticIds = new Set(optimisticLogs.map((entry) => entry.id));

  return [...optimisticLogs, ...serverLogs.filter((log) => !optimisticIds.has(log.id))].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
};

const prunePendingOptimisticEntries = (
  pendingOptimisticEntries: Record<string, EnrichedActivityLog[]>,
): Record<string, EnrichedActivityLog[]> => {
  const now = Date.now();
  return Object.fromEntries(
    Object.entries(pendingOptimisticEntries)
      .map(([employeeId, entries]) => [
        employeeId,
        entries.filter((entry) => {
          const timestamp = new Date(entry.timestamp).getTime();
          return Number.isFinite(timestamp) && now - timestamp <= OPTIMISTIC_ACTIVITY_LOG_TTL_MS;
        }),
      ])
      .filter(([, entries]) => entries.length > 0),
  );
};

const buildById = (logs: EnrichedActivityLog[]): Record<string, EnrichedActivityLog> =>
  logs.reduce(
    (acc, log) => {
      acc[log.id] = log;
      return acc;
    },
    {} as Record<string, EnrichedActivityLog>,
  );

const initialState: ActivityLogState = {
  serverLogs: [],
  logs: [],
  byId: {},
  loading: false,
  error: null,
  filters: {},
  pendingOptimisticEntries: {},
  entityLogs: [],
  entityLoading: false,
};

export const getActivityLogs = createAppThunk<
  EnrichedActivityLog[],
  { organizationId: string; token: string }
>('activityLogs/getActivityLogs', async ({ organizationId, token }, thunkAPI) => {
  const { data, error } = await fetchActivityLogs(organizationId, token, true);

  if (error) {
    return thunkAPI.rejectWithValue(error);
  }
  return data as EnrichedActivityLog[];
});

export const getEntityActivityLogs = createAppThunk<
  EnrichedActivityLog[],
  { entityId: string; organizationId: string; token: string }
>('activityLogs/getEntityActivityLogs', async ({ entityId, organizationId, token }, thunkAPI) => {
  const { data, error } = await fetchActivityLogsByEntityId(entityId, organizationId, token);

  if (error) {
    return thunkAPI.rejectWithValue(error);
  }
  return (data ?? []) as EnrichedActivityLog[];
});

export const activityLogSlice = createSlice({
  name: 'activityLog',
  initialState,
  reducers: {
    clearActivityLogs: (state) => {
      state.serverLogs = [];
      state.logs = [];
      state.byId = {};
      state.pendingOptimisticEntries = {};
    },
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    clearEntityLogs: (state) => {
      state.entityLogs = [];
    },
    addOptimisticEntry: (
      state,
      action: PayloadAction<{ employeeId: string; entry: EnrichedActivityLog }>,
    ) => {
      const { employeeId, entry } = action.payload;
      state.pendingOptimisticEntries = prunePendingOptimisticEntries(
        state.pendingOptimisticEntries,
      );
      if (!state.pendingOptimisticEntries[employeeId]) {
        state.pendingOptimisticEntries[employeeId] = [];
      }
      state.pendingOptimisticEntries[employeeId].unshift(entry);
      state.logs = mergeActivityLogs(state.serverLogs, state.pendingOptimisticEntries);
      state.byId = buildById(state.logs);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActivityLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActivityLogs.fulfilled, (state, action) => {
        state.loading = false;

        // Sort logs by timestamp, newest first
        const sortedLogs = [...action.payload].sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );

        state.serverLogs = sortedLogs;
        state.pendingOptimisticEntries = prunePendingOptimisticEntries(
          state.pendingOptimisticEntries,
        );
        state.logs = mergeActivityLogs(state.serverLogs, state.pendingOptimisticEntries);
        state.byId = buildById(state.logs);
      })
      .addCase(getActivityLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(getEntityActivityLogs.pending, (state) => {
        state.entityLoading = true;
      })
      .addCase(getEntityActivityLogs.fulfilled, (state, action) => {
        state.entityLoading = false;
        state.entityLogs = [...action.payload].sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );
      })
      .addCase(getEntityActivityLogs.rejected, (state) => {
        state.entityLoading = false;
      });
  },
});

export const { clearActivityLogs, setFilters, clearFilters, addOptimisticEntry, clearEntityLogs } =
  activityLogSlice.actions;

export default activityLogSlice.reducer;
